<template>
  <div class="size-profile-page">
    <div class="container">
      <div class="page-intro">
      <h2>我的尺码管理</h2>
      <p class="subtitle">保存多条尺码数据，在智能推荐中灵活使用（最多保存10条）</p>
      
      <div v-if="loading" class="loading">加载中...</div>
      
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>
      
      <div v-if="success" class="alert alert-success">
        {{ successMessage }}
      </div>

      </div>
      <template v-if="!loading && isCustomerLoggedIn">
        <div class="records-toolbar">
          <button class="btn btn-primary" @click="openCreateForm" :disabled="sizeRecords.length >= 10">
            新建尺码记录
          </button>
          <span class="record-count">已保存 {{ sizeRecords.length }}/10</span>
        </div>

        <!-- 已保存的尺码记录列表 -->
        <div class="records-section">
          <h3>已保存的尺码记录</h3>
          
          <div v-if="sizeRecords.length === 0" class="no-records">
            <p>尚未保存任何尺码记录，点击“新建尺码记录”开始添加。</p>
          </div>
          
          <div v-else class="records-list">
            <div 
              v-for="(record, index) in sizeRecords" 
              :key="record.id" 
              class="record-card"
              :class="{ dragging: draggingIndex === index, 'drag-over': dragOverIndex === index }"
              draggable="true"
              @dragstart="onDragStart(index)"
              @dragend="onDragEnd"
              @dragover.prevent="onDragOver(index)"
              @dragleave="onDragLeave"
              @drop="onDrop(index)"
            >
              <div class="record-header">
                <div class="drag-handle" title="拖拽排序" aria-hidden="true"></div>
                <div class="record-label">
                  {{ record.sizeLabel || '默认' }}
                  <span class="record-date">{{ formatDate(record.updatedAt) }}</span>
                </div>
                <div class="record-actions">
                  <button class="btn-icon edit-btn" @click="editRecord(record)" title="编辑"></button>
                  <button class="btn-icon delete-btn" @click="deleteRecord(record.id)" title="删除"></button>
                </div>
              </div>
              
              <div class="record-content">
                <div class="record-info">
                  <span class="info-item">
                    <strong>身高:</strong> {{ record.height }} cm
                  </span>
                  <span class="info-item">
                    <strong>体重:</strong> {{ record.weight }} kg
                  </span>
                  <span class="info-item" v-if="record.gender">
                    <strong>性别:</strong> {{ record.gender }}
                  </span>
                  <span class="info-item" v-if="record.fitPreference">
                    <strong>穿衣偏好:</strong> {{ record.fitPreference }}
                  </span>
                  <span class="info-item" v-if="record.bodyType">
                    <strong>体型:</strong> {{ record.bodyType }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showForm || editingId" class="size-form-card card">
          <!-- 自动保存状态条 -->
          <div v-if="showForm && !editingId" class="auto-save-bar">
            <div class="auto-save-status">
              <span v-if="autoSaveStatus === 'saving'" class="status-icon saving" aria-hidden="true"></span>
              <span v-else-if="autoSaveStatus === 'saved'" class="status-icon saved" aria-hidden="true"></span>
              <span v-else-if="autoSaveStatus === 'error'" class="status-icon error" aria-hidden="true"></span>
              <span class="status-text">
                <template v-if="autoSaveStatus === 'saving'">保存中...</template>
                <template v-else-if="autoSaveStatus === 'saved'">已保存</template>
                <template v-else-if="autoSaveStatus === 'error'">保存失败</template>
                <template v-else>自动保存已开启</template>
              </span>
              <span v-if="lastSavedTime" class="status-time">
                {{ formatTime(lastSavedTime) }}
              </span>
            </div>
          </div>
          <h3>{{ editingId ? '编辑尺码记录' : '新增尺码记录' }}</h3>
          <form @submit.prevent="saveSizeRecord">
            <div class="form-row">
              <div class="form-group">
                <label for="height">身高 (cm) *</label>
                <input type="number" id="height" v-model.number="formData.height" min="100" max="250" required>
              </div>
              
              <div class="form-group">
                <label for="weight">体重 (kg) *</label>
                <input type="number" id="weight" v-model.number="formData.weight" min="30" max="200" required>
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label for="gender">性别</label>
                <select id="gender" v-model="formData.gender">
                  <option value="">请选择</option>
                  <option value="男">男</option>
                  <option value="女">女</option>
                </select>
              </div>
              
              <div class="form-group">
                <label for="fitPreference">穿衣偏好</label>
                <select id="fitPreference" v-model="formData.fitPreference">
                  <option value="">请选择</option>
                  <option value="修身">修身</option>
                  <option value="正常">正常</option>
                  <option value="宽松">宽松</option>
                </select>
              </div>

              <div class="form-group">
                <label for="bodyType">体型</label>
                <select id="bodyType" v-model="formData.bodyType">
                  <option value="">请选择</option>
                  <option value="偏瘦">偏瘦</option>
                  <option value="标准">标准</option>
                  <option value="偏胖">偏胖</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label for="sizeLabel">标签（可选）</label>
              <input type="text" id="sizeLabel" v-model="formData.sizeLabel" 
                     placeholder="如：冬季、健身后、正常体重等">
              <p class="help-text">为这条尺码记录添加标签，便于区分</p>
            </div>
            
            <div class="form-actions">
              <button type="submit" class="btn btn-primary" 
                      :disabled="!formData.height || !formData.weight">
                {{ editingId ? '更新' : '保存' }}
              </button>
              <button type="button" class="btn btn-secondary" 
                      @click="cancelEdit">
                取消
              </button>
              <router-link to="/" class="btn btn-secondary">返回首页</router-link>
            </div>
          </form>
        </div>
      </template>

      
      <div v-if="!isCustomerLoggedIn" class="login-prompt card">
        <h3>请先登录</h3>
        <p>您需要登录后才能管理尺码信息</p>
        <router-link to="/login" class="btn btn-primary">去登录</router-link>
      </div>
      
      <!-- 撤销删除提示 -->
      <div v-if="showUndoToast" class="undo-toast">
        <span>已删除尺码记录</span>
        <button class="undo-btn" @click="undoRemove">撤销</button>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, watch } from 'vue';

export default {
  name: 'CustomerSizeProfile',
  data() {
    return {
      isCustomerLoggedIn: false,
      customerUsername: '',
      loading: true,
      error: '',
      success: false,
      successMessage: '',
      
      // 表单数据（使用reactive便于自动保存）
      formData: reactive({
        height: null,
        weight: null,
        gender: '',
        fitPreference: '',
        bodyType: '',
        sizeLabel: ''
      }),
      
      // 尺码记录列表
      sizeRecords: [],
      
      // 编辑状态
      editingId: null,
      showForm: false,
      
      // 自动保存相关
      autoSaveStatus: 'idle',
      lastSavedTime: null,
      
      // 撤销删除功能
      deletedRecords: [],
      showUndoToast: false,
      undoTimer: null,
      
      // 拖拽排序相关
      draggingIndex: -1,
      dragOverIndex: -1
    }
  },
  
  created() {
    this.checkLoginStatus();
    if (this.isCustomerLoggedIn) {
      this.fetchSizeRecords();
    } else {
      this.loading = false;
    }
    this.initAutoSave();
  },
  
  mounted() {
    // 监听表单数据变化，触发自动保存
    this.formWatcher = watch(
      () => ({ ...this.formData }),
      () => {
        if (this.showForm && !this.editingId) {
          this.triggerAutoSave();
        }
      },
      { deep: true }
    );
  },
  
  beforeUnmount() {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    if (this.formWatcher) {
      this.formWatcher();
    }
  },
  
  methods: {
    // 初始化自动保存
    initAutoSave() {
      const savedData = localStorage.getItem('size_profile_auto_save');
      if (savedData) {
        try {
          const parsed = JSON.parse(savedData);
          // 只在新建表单时加载自动保存的数据
          if (!this.editingId && !this.showForm) {
            Object.keys(parsed).forEach(key => {
              if (Object.prototype.hasOwnProperty.call(this.formData, key)) {
                this.formData[key] = parsed[key];
              }
            });
          }
        } catch (e) {
          console.error('加载自动保存数据失败:', e);
        }
      }
    },
    
    // 触发自动保存（防抖）
    triggerAutoSave() {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer);
      }
      this.autoSaveStatus = 'saving';
      this.autoSaveTimer = setTimeout(() => {
        try {
          localStorage.setItem('size_profile_auto_save', JSON.stringify(this.formData));
          this.autoSaveStatus = 'saved';
          this.lastSavedTime = new Date();
        } catch (e) {
          console.error('自动保存失败:', e);
          this.autoSaveStatus = 'error';
        }
      }, 2000);
    },
    
    // 清除自动保存数据
    clearAutoSave() {
      localStorage.removeItem('size_profile_auto_save');
      this.autoSaveStatus = 'idle';
      this.lastSavedTime = null;
    },
    
    // 检查登录状态
    checkLoginStatus() {
      this.isCustomerLoggedIn = !!localStorage.getItem('customerToken');
      this.customerUsername = localStorage.getItem('customerUsername') || '';
    },
    
    // 客户退出登录
    customerLogout() {
      localStorage.removeItem('customerToken');
      localStorage.removeItem('customerUsername');
      this.isCustomerLoggedIn = false;
      this.customerUsername = '';
      this.$router.push('/login');
    },
    
    // 获取尺码记录列表
    async fetchSizeRecords() {
      try {
        this.loading = true;
        const username = this.customerUsername;
        const response = await this.$axios.get('/customers/size-history', {
          headers: {
            'X-Username': username
          }
        });
        
        if (response.data.success) {
          this.sizeRecords = response.data.data || [];
          this.error = '';
        } else {
          this.error = response.data.message || '获取记录失败';
        }
      } catch (err) {
        console.error('获取尺码记录失败:', err);
        this.error = '获取尺码记录失败';
      } finally {
        this.loading = false;
      }
    },
    
    // 保存或更新尺码记录
    async saveSizeRecord() {
      try {
        // 验证必填项
        if (!this.formData.height || !this.formData.weight) {
          this.error = '身高和体重为必填项';
          return;
        }

        const username = this.customerUsername;
        
        if (this.editingId) {
          // 更新现有记录
          const response = await this.$axios.put(
            `/customers/size-history/${this.editingId}`,
            this.formData,
            {
              headers: {
                'X-Username': username
              }
            }
          );
          
          if (response.data.success) {
            this.successMessage = '尺码记录已更新';
            this.success = true;
            this.showForm = false;
            this.clearForm();
            this.fetchSizeRecords();
          } else {
            this.error = response.data.message || '更新失败';
          }
        } else {
          // 保存新记录
          const response = await this.$axios.post(
            '/customers/size-history',
            this.formData,
            {
              headers: {
                'X-Username': username
              }
            }
          );
          
          if (response.data.success) {
            this.successMessage = '尺码记录已保存';
            this.success = true;
            this.showForm = false;
            this.clearForm();
            this.clearAutoSave(); // 清除自动保存数据
            this.fetchSizeRecords();
          } else {
            this.error = response.data.message || '保存失败';
          }
        }
        
        setTimeout(() => {
          this.success = false;
        }, 3000);
      } catch (err) {
        console.error('保存尺码记录失败:', err);
        this.error = err.response?.data?.message || '保存失败';
      }
    },
    
    // 打开新建表单
    openCreateForm() {
      this.showForm = true;
      this.editingId = null;
      this.clearForm();
      setTimeout(() => {
        const el = document.querySelector('.size-form-card');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },

    // 编辑记录
    editRecord(record) {
      this.showForm = true;
      this.editingId = record.id;
      this.formData = {
        height: record.height,
        weight: record.weight,
        gender: record.gender || '',
        fitPreference: record.fitPreference || '',
        bodyType: record.bodyType || '',
        sizeLabel: record.sizeLabel || ''
      };
      // 滚动到表单
      setTimeout(() => {
        const el = document.querySelector('.size-form-card');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    },
    
    // 取消编辑或新建
    cancelEdit() {
      this.showForm = false;
      this.editingId = null;
      this.clearForm();
    },
    
    // 清空表单
    clearForm() {
      this.formData.height = null;
      this.formData.weight = null;
      this.formData.gender = '';
      this.formData.fitPreference = '';
      this.formData.bodyType = '';
      this.formData.sizeLabel = '';
      this.editingId = null;
    },
    
    // 删除记录
    async deleteRecord(recordId) {
      // 找到要删除的记录
      const recordToRemove = this.sizeRecords.find(r => r.id === recordId);
      if (!recordToRemove) return;
      
      try {
        const username = this.customerUsername;
        const response = await this.$axios.delete(
          `/customers/size-history/${recordId}`,
          {
            headers: {
              'X-Username': username
            }
          }
        );
        
        if (response.data.success) {
          // 从列表中移除
          this.sizeRecords = this.sizeRecords.filter(r => r.id !== recordId);
          this.successMessage = '尺码记录已删除';
          this.success = true;
          if (this.editingId === recordId) {
            this.clearForm();
          }
          
          // 保存删除的记录用于撤销
          this.deletedRecords.push(recordToRemove);
          this.showUndoToast = true;
          
          // 清除之前的定时器
          if (this.undoTimer) {
            clearTimeout(this.undoTimer);
          }
          
          // 3秒后隐藏撤销提示
          this.undoTimer = setTimeout(() => {
            this.showUndoToast = false;
            this.deletedRecords = [];
          }, 3000);
        } else {
          this.error = response.data.message || '删除失败';
        }
        
        setTimeout(() => {
          this.success = false;
        }, 3000);
      } catch (err) {
        console.error('删除尺码记录失败:', err);
        this.error = err.response?.data?.message || '删除失败';
      }
    },
    
    // 撤销删除
    async undoRemove() {
      if (this.deletedRecords.length === 0) return;
      
      try {
        const username = this.customerUsername;
        
        // 重新添加所有删除的记录
        for (const record of this.deletedRecords) {
          await this.$axios.post(
            '/customers/size-history',
            {
              height: record.height,
              weight: record.weight,
              gender: record.gender || '',
              fitPreference: record.fitPreference || '',
              bodyType: record.bodyType || '',
              sizeLabel: record.sizeLabel || ''
            },
            {
              headers: {
                'X-Username': username
              }
            }
          );
        }
        
        // 重新获取尺码记录
        await this.fetchSizeRecords();
        
        // 清除撤销状态
        this.showUndoToast = false;
        this.deletedRecords = [];
        if (this.undoTimer) {
          clearTimeout(this.undoTimer);
          this.undoTimer = null;
        }
        
        this.successMessage = '已恢复删除的尺码记录';
        this.success = true;
        setTimeout(() => {
          this.success = false;
        }, 3000);
      } catch (error) {
        console.error('撤销删除失败:', error);
        this.error = '撤销删除失败，请稍后重试';
      }
    },
    
    // 拖拽排序方法
    onDragStart(index) {
      this.draggingIndex = index;
    },
    
    onDragEnd() {
      this.draggingIndex = -1;
      this.dragOverIndex = -1;
    },
    
    onDragOver(index) {
      if (this.draggingIndex !== index) {
        this.dragOverIndex = index;
      }
    },
    
    onDragLeave() {
      this.dragOverIndex = -1;
    },
    
    async onDrop(targetIndex) {
      if (this.draggingIndex === -1 || this.draggingIndex === targetIndex) {
        this.dragOverIndex = -1;
        return;
      }
      
      // 移动记录
      const draggedRecord = this.sizeRecords.splice(this.draggingIndex, 1)[0];
      this.sizeRecords.splice(targetIndex, 0, draggedRecord);
      
      // 更新排序到服务器
      await this.updateOrder();
      
      this.draggingIndex = -1;
      this.dragOverIndex = -1;
    },
    
    async updateOrder() {
      try {
        const username = this.customerUsername;
        const orderData = this.sizeRecords.map((record, index) => ({
          id: record.id,
          sortOrder: index + 1
        }));
        
        await this.$axios.post(
          '/customers/size-history/update-order',
          { records: orderData },
          {
            headers: {
              'X-Username': username
            }
          }
        );
        
        this.successMessage = '排序已更新';
        this.success = true;
        setTimeout(() => {
          this.success = false;
        }, 2000);
      } catch (error) {
        console.error('更新排序失败:', error);
        this.error = '更新排序失败，请稍后重试';
      }
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    // 格式化时间（用于自动保存状态）
    formatTime(date) {
      if (!date) return '';
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  }
}
</script>

<style scoped>
.size-profile-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(255, 255, 255, 0.96), rgba(241, 245, 249, 0.94)),
    linear-gradient(180deg, #eef2f7 0%, #f8fafc 100%);
}

/* 自动保存状态条 */
.auto-save-bar {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.75rem 1rem;
  margin: -1.5rem -1.5rem 1.5rem;
  border-radius: 8px 8px 0 0;
}

.auto-save-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.status-icon {
  position: relative;
  width: 18px;
  height: 18px;
  font-size: 0;
}

.status-icon.saving::before,
.status-icon.saved::before,
.status-icon.error::before,
.status-icon.error::after {
  content: '';
  position: absolute;
}

.status-icon.saving::before {
  inset: 2px;
  border: 2px solid rgba(245, 158, 11, 0.25);
  border-top-color: #f59e0b;
  border-radius: 999px;
  animation: spin 0.9s linear infinite;
}

.status-icon.saved::before {
  top: 4px;
  left: 5px;
  width: 8px;
  height: 4px;
  border-left: 2px solid #10b981;
  border-bottom: 2px solid #10b981;
  transform: rotate(-45deg);
}

.status-icon.error::before,
.status-icon.error::after {
  top: 8px;
  left: 3px;
  width: 12px;
  height: 2px;
  background: #ef4444;
}

.status-icon.error::before {
  transform: rotate(45deg);
}

.status-icon.error::after {
  transform: rotate(-45deg);
}

.status-text {
  color: #666;
}

.status-time {
  color: #999;
  margin-left: auto;
}

.top-nav {
  background-color: #fff;
  border-bottom: 1px solid #ddd;
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
}

.shop-title {
  font-size: 1.5rem;
  color: #f44336;
  margin: 0;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.nav-link {
  color: #333;
  text-decoration: none;
  transition: color 0.3s;
}

.nav-link:hover {
  color: #f44336;
}

.nav-user {
  color: #f44336;
  font-weight: bold;
}

.nav-divider {
  color: #ddd;
}

.container {
  max-width: 1080px;
  margin: 2rem auto;
  padding: 0 1.5rem;
}

.page-intro {
  margin-bottom: 1.8rem;
  text-align: center;
}

.container h2 {
  text-align: center;
  margin-bottom: 0.5rem;
  color: #111827;
  font-size: clamp(2rem, 3vw, 2.8rem);
  letter-spacing: -0.04em;
}

.subtitle {
  text-align: center;
  color: #64748b;
  margin-bottom: 0;
  font-size: 0.95rem;
}

.records-toolbar,
.records-section,
.size-form-card,
.login-prompt,
.no-records,
.undo-toast {
  border: 1px solid rgba(255, 255, 255, 0.72);
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow: var(--shadow-soft);
}

.size-form-card {
  padding: 2rem;
  border-radius: 28px;
  margin-bottom: 2rem;
}

.records-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.2rem;
  border-radius: 24px;
  margin-bottom: 1.25rem;
}

.size-form-card h3 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #333;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 16px;
  font-size: 1rem;
  font-family: inherit;
  background: rgba(255, 255, 255, 0.92);
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.help-text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #666;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.records-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: 28px;
}

.records-section h3 {
  margin-bottom: 1.5rem;
  color: #111827;
}

.record-count {
  color: #475569;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.05);
}

.no-records {
  padding: 2rem;
  border-radius: 22px;
  text-align: center;
  color: #999;
}

.records-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.record-card {
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  padding: 1.5rem;
  transition: all 0.3s;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.06);
}

.record-card:hover {
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.1);
  border-color: #4a90e2;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.drag-handle {
  cursor: grab;
  width: 40px;
  height: 40px;
  padding: 0;
  color: transparent;
  font-size: 0;
  margin-right: 1rem;
  transition: background-color 0.2s;
  user-select: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.05);
}

.drag-handle:hover {
  background: rgba(15, 23, 42, 0.08);
}

.drag-handle:active {
  cursor: grabbing;
}

.drag-grip {
  position: relative;
  width: 12px;
  height: 16px;
}

.drag-handle::before,
.drag-handle::after {
  content: '';
  position: absolute;
  top: 14px;
  width: 3px;
  height: 3px;
  border-radius: 999px;
  background: #475569;
  box-shadow: 0 6px 0 #475569, 0 12px 0 #475569;
}

.drag-handle::before {
  left: 14px;
}

.drag-handle::after {
  right: 14px;
}

.record-card.dragging {
  opacity: 0.5;
  transform: rotate(3deg);
  box-shadow: none;
}

.record-card.drag-over {
  border-color: #ff4400;
  border-width: 2px;
  background-color: #fff8f0;
}

.record-label {
  font-size: 1.1rem;
  font-weight: bold;
  color: #111827;
}

.record-date {
  display: block;
  font-size: 0.85rem;
  color: #999;
  font-weight: normal;
  margin-top: 0.3rem;
}

.record-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.2);
  width: 40px;
  height: 40px;
  border-radius: 14px;
  font-size: 0;
  cursor: pointer;
  padding: 0;
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.2s;
  position: relative;
}

.btn-icon:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.08);
}

.btn-icon::before,
.btn-icon::after {
  content: '';
  position: absolute;
}

.edit-btn::before {
  top: 20px;
  left: 11px;
  width: 16px;
  height: 3px;
  background: #2563eb;
  transform: rotate(-45deg);
  border-radius: 999px;
}

.edit-btn::after {
  top: 12px;
  right: 9px;
  width: 5px;
  height: 5px;
  border: 2px solid #2563eb;
  border-left: 0;
  border-bottom: 0;
  transform: rotate(-45deg);
}

.delete-btn::before {
  top: 13px;
  left: 13px;
  width: 14px;
  height: 14px;
  border: 2px solid #dc2626;
  border-top: 0;
  border-radius: 0 0 4px 4px;
}

.delete-btn::after {
  top: 11px;
  left: 14px;
  width: 12px;
  height: 2px;
  background: #dc2626;
  box-shadow: 2px -3px 0 -1px #dc2626;
}

.record-content {
  margin: 0;
}

.record-info {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.info-item {
  flex: 1;
  min-width: 150px;
  font-size: 0.95rem;
  color: #555;
}

.login-prompt {
  padding: 3rem;
  border-radius: 28px;
  text-align: center;
}

.login-prompt h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #111827;
}

.login-prompt p {
  margin-bottom: 2rem;
  color: #666;
}

.loading {
  text-align: center;
  padding: 4rem;
  font-size: 1.2rem;
  color: #666;
}

.alert {
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn {
  background-color: #111113;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 999px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s;
  text-decoration: none;
  display: inline-block;
}

.btn:hover:not(:disabled) {
  background-color: #2c2c2e;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: rgba(148, 163, 184, 0.16);
  color: #111827;
  border: 1px solid rgba(148, 163, 184, 0.24);
}

.btn-secondary:hover {
  background-color: rgba(148, 163, 184, 0.24);
}

.btn-primary {
  background: linear-gradient(135deg, #111113, #2c2c2e);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2c2c2e, #444447);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }

  .records-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  
  .size-form-card {
    padding: 1.5rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .form-actions .btn {
    width: 100%;
  }
  
  .record-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .record-actions {
    margin-top: 1rem;
  }
  
  .record-info {
    flex-direction: column;
  }
  
  .info-item {
    min-width: auto;
  }
}

/* 撤销删除提示条 */
.undo-toast {
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.undo-toast span {
  font-size: 0.95rem;
}

.undo-btn {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.undo-btn:hover {
  background-color: #45a049;
}
</style>
